import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'What is Traction Bee?',
    answer: 'Traction Bee is an AI-powered growth platform for pre-revenue tech founders. It uses autonomous AI agents to find high-value prospects and generate personalized outreach emails, putting your sales outreach on autopilot.',
  },
  {
    question: 'How does the AI find leads?',
    answer: 'Our Scout agent queries Apollo.io and monitors buying signals like funding announcements, hiring patterns, and technology adoption. It matches prospects against your Ideal Customer Profile (ICP) and scores them based on signal strength and recency.',
  },
  {
    question: 'Are emails sent automatically?',
    answer: 'No. Traction Bee is human-in-the-loop by design. AI drafts personalized emails, but you review and approve each one before sending. This ensures authenticity and compliance while saving you hours of writing time.',
  },
  {
    question: 'How does the Gmail integration work?',
    answer: 'Our Chrome extension adds a Traction Bee sidebar to Gmail. Each morning, you will see your daily leads with AI-drafted emails. Click to review, edit if needed, and send directly from your Gmail account.',
  },
  {
    question: 'What data sources do you use?',
    answer: 'We integrate with Apollo.io for contact data and LinkedIn for professional context. We also monitor public signals like Crunchbase funding announcements and job postings.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes. We use enterprise-grade security with Row Level Security (RLS) in our database, encrypted connections, and never share your data with third parties. Your Gmail credentials are handled by Google OAuth.',
  },
  {
    question: 'How many leads do I get per day?',
    answer: 'The default is 3 high-quality leads per day, each with a personalized email draft. This is intentionally limited to maintain quality over quantity and respect inbox deliverability.',
  },
  {
    question: 'Can I upload my own contacts?',
    answer: 'Yes. You can upload a CSV of existing contacts, and our AI will enrich them with additional data and generate personalized outreach for each one.',
  },
  {
    question: 'What is the free trial?',
    answer: 'New users get a 14-day free trial with full access to all features. No credit card required to start.',
  },
  {
    question: 'How do I get support?',
    answer: 'Email us at support@tractionbee.com or use the Support page. We typically respond within 24 hours.',
  },
];

export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="py-12 bg-cacao-dark">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">Frequently Asked Questions</h1>
          <p className="mt-2 text-sm text-cacao-muted">
            Everything you need to know about Traction Bee
          </p>
        </div>

        <div className="mt-8 space-y-2">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <span className="text-sm font-medium text-[#F5F5F5]">{faq.question}</span>
                <ChevronDown
                  className={`h-4 w-4 text-cacao-muted transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-4 pb-3">
                  <p className="text-sm text-cacao-muted leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-lg bg-cacao-dark-secondary border border-cacao-dark-tertiary p-5 text-center">
          <h2 className="font-heading text-sm font-medium text-[#F5F5F5]">Still have questions?</h2>
          <p className="mt-1 text-xs text-cacao-muted">
            Our support team is here to help.
          </p>
          <Link
            to={ROUTES.SUPPORT}
            className="mt-3 inline-block rounded-md bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
}
